import {
  Detector,
  History,
  LinkedAsset,
  Releases,
  Sort,
  Status,
} from 'constants/filters'
import {
  BoolType,
  ContentType,
  DistributionStatusLabel,
  IdVerificationStatusFilter as IdVerificationStatus,
  LicenseType,
  ProgramType,
  ReviewType,
} from 'constants/resources'
import { format, parseISO } from 'date-fns'
import { DateRange } from 'helpers/dates'
import { isEqual } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { parse, stringify } from 'qs'

import { Rating, ResourceFileType } from 'types'
import {
  GetApiV1AdminResourcesSearchQueryParams,
  GetApiV1ResourcesQueryParams,
} from 'types/api'

export enum SearchBy {
  ContributorEmail = 'user_email',
  ReviewerEmail = 'reviewer_email',
  Keyword = 'keywords',
  Assignment = 'assignment',
  ID = 'import_guid',
  Fingerprint = 'source_file_fingerprint',
}

export type ResourcesFilter = {
  [key: string]: unknown
  category_id: string[]
  content_type: ContentType[]
  license: LicenseType[]
  program_type: ProgramType[]
  file_type: ResourceFileType[]
  sort: Sort
  status: Status[]
  rating: (Rating | 'none')[]
  releases: Releases[]
  review_type: ReviewType[]
  id_verification_status: IdVerificationStatus[]
  file_history: History[]
  uploaded?: DateRange
  approved?: DateRange
  reviewed?: DateRange
  uploaded_at?: DateRange
  approved_at?: DateRange
  reviewed_at?: DateRange
  nonce?: string
  search_field: SearchBy
  query: string
  ai_generated: BoolType[]
  duplicate: BoolType[]
  linked_asset: LinkedAsset[]
  auto_submitted: BoolType[]
  magic_metadata: BoolType[]
  auto_distribute: BoolType[]
  distribution_status: string
  marketplace_ids: string[]
  detector: Detector
  user_email?: string[]
  reviewer_email?: string[]
  title?: string[]
  keywords?: string[]
  assignment?: string[]
  import_guid?: string[]
  source_file_fingerprint?: string[]
}

const initFilter = (filter: ResourcesFilter): ResourcesFilter => {
  const query = parse(window.location.search.replace('?', ''))

  return Object.keys(filter).reduce((acc, key) => {
    if (key === 'sort') {
      if ('sort_field' in query && 'sort_dir' in query) {
        if (query['sort_field'] === 'created_at') {
          acc[key] = query['sort_dir'] === 'desc' ? Sort.Newest : Sort.Oldest
        } else if (query['sort_field'] === 'title') {
          acc[key] =
            query['sort_dir'] === 'desc' ? Sort.TitleDesc : Sort.TitleAsc
        } else if (query['sort_field'] === 'lifetime_downloads') {
          acc[key] =
            query['sort_dir'] === 'desc'
              ? Sort.DownloadsDesc
              : Sort.DownloadsAsc
        }
      } else {
        acc[key] = Sort.Newest
      }
    } else if (key === 'uploaded') {
      acc.uploaded = {}

      if ('created_at_start' in query && 'created_at_end' in query) {
        acc.uploaded.from = parseISO(query['created_at_start'] as string)
        acc.uploaded.to = parseISO(query['created_at_end'] as string)
      } else if ('created_at_start' in query) {
        acc.uploaded.from = parseISO(query['created_at_start'] as string)
        acc.uploaded.to = undefined
      } else if ('created_at_end' in query) {
        acc.uploaded.from = undefined
        acc.uploaded.to = parseISO(query['created_at_end'] as string)
      } else {
        acc.uploaded = filter[key]
      }
    } else if (key === 'reviewed') {
      acc.reviewed = {}

      if ('reviewed_at_start' in query && 'reviewed_at_end' in query) {
        acc.reviewed.from = parseISO(query['reviewed_at_start'] as string)
        acc.reviewed.to = parseISO(query['reviewed_at_end'] as string)
      } else if ('reviewed_at_start' in query) {
        acc.reviewed.from = parseISO(query['reviewed_at_start'] as string)
        acc.reviewed.to = undefined
      } else if ('reviewed_at_end' in query) {
        acc.reviewed.from = undefined
        acc.reviewed.to = parseISO(query['reviewed_at_end'] as string)
      } else {
        acc.reviewed = filter[key]
      }
    } else if (key === 'approved') {
      acc.approved = {}

      if ('approved_at_start' in query && 'approved_at_end' in query) {
        acc.approved.from = parseISO(query['approved_at_start'] as string)
        acc.approved.to = parseISO(query['approved_at_end'] as string)
      } else if ('approved_at_start' in query) {
        acc.approved.from = parseISO(query['approved_at_start'] as string)
        acc.approved.to = undefined
      } else if ('approved_at_end' in query) {
        acc.approved.from = undefined
        acc.approved.to = parseISO(query['approved_at_end'] as string)
      } else {
        acc.approved = filter[key]
      }
    } else if (key === 'releases') {
      if ('release_status' in query) {
        acc[key] = query['release_status']
          ? [Releases.With]
          : [Releases.Without]
      } else {
        acc[key] = filter[key]
      }
    } else if (key === 'file_history') {
      if ('file_history' in query) {
        acc[key] = query['file_history'] ? [History.With] : [History.Without]
      } else {
        acc[key] = filter[key]
      }
    } else if (key === 'status') {
      if ('state' in query) {
        const newStatus = Array.isArray(query['state'])
          ? query['state']
          : [query['state']]
        acc['status'] = newStatus.map(v => {
          switch (v) {
            case 'submitted':
              return Status.PendingReview
            case 'needs_corrections':
              return Status.ActionNeeded
            default:
              return v
          }
        }) as Status[]
      } else {
        acc['status'] = filter['status']
      }
    } else if (key === 'detector') {
      if ('ai_detected' in query) {
        acc[key] =
          query['ai_detected'] === 'true'
            ? Detector.AIgenerated
            : query['ai_detected'] === 'false'
            ? Detector.NotAIgenerated
            : filter[key]
      } else {
        acc[key] = filter[key]
      }
    } else {
      acc[key] = query[key] || filter[key]
    }

    return acc
  }, {} as ResourcesFilter)
}

export default class FilterStore {
  @observable defaultFilter: ResourcesFilter = {
    category_id: [],
    content_type: [],
    license: [],
    program_type: [],
    file_type: [],
    sort: Sort.Newest,
    status: [
      Status.PendingReview,
      Status.Flagged,
      Status.ActionNeeded,
      Status.Approving,
      Status.Approved,
      Status.Published,
      Status.Rejected,
    ],
    rating: [],
    releases: [],
    review_type: [],
    id_verification_status: [],
    file_history: [],
    uploaded: undefined,
    approved: undefined,
    reviewed: undefined,
    search_field: SearchBy.ContributorEmail,
    query: '',
    ai_generated: [],
    duplicate: [],
    linked_asset: [],
    auto_submitted: [],
    magic_metadata: [],
    auto_distribute: [],
    distribution_status: DistributionStatusLabel.Transferred,
    marketplace_ids: [],
    detector: Detector.None,
    user_email: [],
    reviewer_email: [],
    title: [],
    keywords: [],
    assignment: [],
    import_guid: [],
    source_file_fingerprint: [],
  }
  @observable filter: ResourcesFilter = initFilter(this.defaultFilter)

  constructor() {
    makeObservable(this)
  }

  @action setFilter = (
    filter: ResourcesFilter,
    syncUrl = false,
    hideStateFromUrl = false
  ): void => {
    this.filter = filter

    if (syncUrl) {
      const queryParams = { ...this.queryParams }

      if (hideStateFromUrl) {
        delete queryParams.state
      }

      const url = `?${stringify(queryParams)}`
      history.pushState({}, '', url)
    }
  }

  @action resetFilters = (hideStateFromUrl = false): void => {
    this.filter = { ...this.defaultFilter }

    const queryParams = { ...this.queryParams }

    if (hideStateFromUrl) {
      delete queryParams.state
    }

    const url = `?${stringify(queryParams)}`
    history.pushState({}, '', url)
  }

  @computed get filterIsDirty(): boolean {
    return Object.entries(this.defaultFilter).some(
      ([key, value]) => !isEqual(this.filter[key], value)
    )
  }

  @computed get queryParams():
    | GetApiV1AdminResourcesSearchQueryParams
    | GetApiV1ResourcesQueryParams {
    const params: GetApiV1AdminResourcesSearchQueryParams &
      GetApiV1ResourcesQueryParams = {}
    const createdAtParams = params.created_at || []
    const approvedAtParams = params.approved_at || []
    const reviewedAtParams = params.reviewed_at || []

    params.created_at = [...createdAtParams]
    params.approved_at = [...approvedAtParams]
    params.reviewed_at = [...reviewedAtParams]

    const statusToStateMap: Record<string, string> = {
      [Status.Submitting]: 'submitting',
      [Status.PendingReview]: 'submitted',
      [Status.AcceptChanges]: 'needs_corrections',
      [Status.ActionNeeded]: 'needs_corrections',
      [Status.Flagged]: 'flagged',
      [Status.Approving]: 'approving',
      [Status.Approved]: 'approved',
      [Status.Exporting]: 'active',
      [Status.Published]: 'active',
      [Status.Rejected]: 'rejected',
      [Status.Started]: 'started',
      [Status.Inactive]: 'inactive',
    }

    if (this.filter.query && this.filter.search_field) {
      params.search_field = this.filter
        .search_field as GetApiV1AdminResourcesSearchQueryParams['search_field']
      params.query = this.filter.query
    }

    if (this.filter.status.length > 0) {
      params.state = this.filter.status.map(
        status => statusToStateMap[status]
      ) as GetApiV1AdminResourcesSearchQueryParams['state']
    }

    if (this.filter.category_id.length) {
      params.category_ids = this.filter.category_id.map(x => String(x))
      params.category_id = this.filter.category_id.map(x => Number(x))
    }

    if (this.filter.content_type.length > 0) {
      params.content_type = this.filter
        .content_type as GetApiV1AdminResourcesSearchQueryParams['content_type']
    }

    if (this.filter.program_type.length > 0) {
      params.program_type = this.filter
        .program_type as GetApiV1AdminResourcesSearchQueryParams['program_type'] &
        GetApiV1ResourcesQueryParams['program_type']
    }

    if (this.filter.license.length > 0) {
      params.license = this.filter
        .license as GetApiV1AdminResourcesSearchQueryParams['license'] &
        GetApiV1ResourcesQueryParams['license']
    }

    if (this.filter.file_type.length > 0) {
      params.file_type = this.filter
        .file_type as GetApiV1AdminResourcesSearchQueryParams['file_type']
    }

    if (this.filter.rating.length > 0) {
      params.rating = this.filter.rating.reduce(
        (acc, rating) => {
          if (rating === 'none') {
            return [...acc, 0]
          } else {
            return [...acc, rating]
          }
        },
        [] as (string | number)[]
      ) as GetApiV1AdminResourcesSearchQueryParams['rating']
    }

    if (this.filter.id_verification_status.length > 0) {
      params.user_id_verification_status = this.filter
        .id_verification_status as GetApiV1AdminResourcesSearchQueryParams['user_id_verification_status']
    }

    if (this.filter.review_type.length > 0) {
      params.review_type = this.filter
        .review_type as GetApiV1AdminResourcesSearchQueryParams['review_type']
    }

    if (this.filter.uploaded) {
      if (
        'from' in this.filter.uploaded &&
        this.filter.uploaded.from instanceof Date
      ) {
        params.created_at_start = format(
          this.filter.uploaded.from,
          'yyyy-MM-dd'
        )
        params.created_at[0] = format(this.filter.uploaded.from, 'yyyy-MM-dd')
        params.created_at[1] = params.created_at[1] || ''
      }

      if (
        'to' in this.filter.uploaded &&
        this.filter.uploaded.to instanceof Date
      ) {
        params.created_at_end = format(this.filter.uploaded.to, 'yyyy-MM-dd')
        params.created_at[1] = format(this.filter.uploaded.to, 'yyyy-MM-dd')
        params.created_at[0] = params.created_at[0] || ''
      }
    }

    if (this.filter.approved) {
      if (
        'from' in this.filter.approved &&
        this.filter.approved.from instanceof Date
      ) {
        params.approved_at_start = format(
          this.filter.approved.from,
          'yyyy-MM-dd'
        )
        params.approved_at[0] = format(this.filter.approved.from, 'yyyy-MM-dd')
        params.approved_at[1] = params.approved_at[1] || ''
      }

      if (
        'to' in this.filter.approved &&
        this.filter.approved.to instanceof Date
      ) {
        params.approved_at_end = format(this.filter.approved.to, 'yyyy-MM-dd')
        params.approved_at[1] = format(this.filter.approved.to, 'yyyy-MM-dd')
        params.approved_at[0] = params.approved_at[0] || ''
      }
    }

    if (this.filter.reviewed) {
      if (
        'from' in this.filter.reviewed &&
        this.filter.reviewed.from instanceof Date
      ) {
        params.reviewed_at_start = format(
          this.filter.reviewed.from,
          'yyyy-MM-dd'
        )
        params.reviewed_at[0] = format(this.filter.reviewed.from, 'yyyy-MM-dd')
        params.reviewed_at[1] = params.reviewed_at[1] || ''
      }

      if (
        'to' in this.filter.reviewed &&
        this.filter.reviewed.to instanceof Date
      ) {
        params.reviewed_at_end = format(this.filter.reviewed.to, 'yyyy-MM-dd')
        params.reviewed_at[1] = format(this.filter.reviewed.to, 'yyyy-MM-dd')
        params.reviewed_at[0] = params.reviewed_at[0] || ''
      }
    }

    if (this.filter.uploaded_at) {
      if (
        'from' in this.filter.uploaded_at &&
        this.filter.uploaded_at.from instanceof Date
      ) {
        params.created_at[0] = format(
          this.filter.uploaded_at.from,
          'yyyy-MM-dd'
        )
        params.created_at[1] = params.created_at[1] || ''
      }

      if (
        'to' in this.filter.uploaded_at &&
        this.filter.uploaded_at.to instanceof Date
      ) {
        params.created_at[1] = format(this.filter.uploaded_at.to, 'yyyy-MM-dd')
        params.created_at[0] = params.created_at[0] || ''
      }
    }

    if (this.filter.approved_at) {
      if (
        'from' in this.filter.approved_at &&
        this.filter.approved_at.from instanceof Date
      ) {
        params.approved_at[0] = format(
          this.filter.approved_at.from,
          'yyyy-MM-dd'
        )
        params.approved_at[1] = params.approved_at[1] || ''
      }

      if (
        'to' in this.filter.approved_at &&
        this.filter.approved_at.to instanceof Date
      ) {
        params.approved_at[1] = format(this.filter.approved_at.to, 'yyyy-MM-dd')
        params.approved_at[0] = params.approved_at[0] || ''
      }
    }

    if (this.filter.reviewed_at) {
      if (
        'from' in this.filter.reviewed_at &&
        this.filter.reviewed_at.from instanceof Date
      ) {
        params.reviewed_at[0] = format(
          this.filter.reviewed_at.from,
          'yyyy-MM-dd'
        )
        params.reviewed_at[1] = params.reviewed_at[1] || ''
      }

      if (
        'to' in this.filter.reviewed_at &&
        this.filter.reviewed_at.to instanceof Date
      ) {
        params.reviewed_at[1] = format(this.filter.reviewed_at.to, 'yyyy-MM-dd')
        params.reviewed_at[0] = params.reviewed_at[0] || ''
      }
    }

    if (this.filter.releases.length === 1) {
      const [release] = this.filter.releases
      params.release_status = release === Releases.With
      params.has_release = release === Releases.With
    }

    if (this.filter.file_history.length === 1) {
      const [fileHistory] = this.filter.file_history
      params.file_history = fileHistory === History.With
    }

    if (this.filter.sort === Sort.Newest) {
      params.sort_field = 'created_at'
      params.sort_dir = 'desc'
      params.sort_direction = 'desc'
    }

    if (this.filter.sort === Sort.Oldest) {
      params.sort_field = 'created_at'
      params.sort_dir = 'asc'
      params.sort_direction = 'asc'
    }

    if (this.filter.sort === Sort.TitleAsc) {
      params.sort_field = 'title'
      params.sort_dir = 'asc'
      params.sort_direction = 'asc'
    }

    if (this.filter.sort === Sort.TitleDesc) {
      params.sort_field = 'title'
      params.sort_dir = 'desc'
      params.sort_direction = 'desc'
    }

    if (this.filter.sort === Sort.DownloadsAsc) {
      params.sort_field =
        'lifetime_downloads' as GetApiV1AdminResourcesSearchQueryParams['sort_field'] &
          GetApiV1ResourcesQueryParams['sort_field']
      params.sort_dir = 'asc'
      params.sort_direction = 'asc'
    }

    if (this.filter.sort === Sort.DownloadsDesc) {
      params.sort_field =
        'lifetime_downloads' as GetApiV1AdminResourcesSearchQueryParams['sort_field'] &
          GetApiV1ResourcesQueryParams['sort_field']
      params.sort_dir = 'desc'
      params.sort_direction = 'desc'
    }

    if (this.filter.sort === Sort.EarningsAsc) {
      params.sort_field =
        'lifetime_downloads' as GetApiV1AdminResourcesSearchQueryParams['sort_field'] &
          GetApiV1ResourcesQueryParams['sort_field']
      params.sort_dir = 'asc'
      params.sort_direction = 'asc'
    }

    if (this.filter.sort === Sort.EarningsDesc) {
      params.sort_field =
        'lifetime_downloads' as GetApiV1AdminResourcesSearchQueryParams['sort_field'] &
          GetApiV1ResourcesQueryParams['sort_field']
      params.sort_dir = 'desc'
      params.sort_direction = 'desc'
    }

    if (this.filter.ai_generated?.length) {
      const arr = this.filter.ai_generated

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.ai_generated = undefined
      } else if (arr.includes(BoolType.True)) {
        params.ai_generated = true
      } else {
        params.ai_generated = false
      }
    }

    if (this.filter.duplicate?.length) {
      const arr = this.filter.duplicate

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.duplicate = undefined
      } else if (arr.includes(BoolType.True)) {
        params.duplicate =
          true as GetApiV1AdminResourcesSearchQueryParams['duplicate'] &
            GetApiV1ResourcesQueryParams['duplicate']
      } else {
        params.duplicate =
          false as GetApiV1AdminResourcesSearchQueryParams['duplicate'] &
            GetApiV1ResourcesQueryParams['duplicate']
      }
    }

    if (this.filter.linked_asset.length === 1) {
      const [linkedAsset] = this.filter.linked_asset
      params.has_svg = linkedAsset === LinkedAsset.SVG
    }

    if (this.filter.auto_submitted?.length) {
      const arr = this.filter.auto_submitted

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.auto_submitted = undefined
      } else if (arr.includes(BoolType.True)) {
        params.auto_submitted = true
      } else {
        params.auto_submitted = false
      }
    }

    if (this.filter.magic_metadata?.length) {
      const arr = this.filter.magic_metadata

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.magic_metadata = undefined
      } else if (arr.includes(BoolType.True)) {
        params.magic_metadata = true
      } else {
        params.magic_metadata = false
      }
    }

    if (this.filter.auto_distribute.length) {
      const arr = this.filter.auto_distribute

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.auto_distribute = undefined
      } else if (arr.includes(BoolType.True)) {
        params.auto_distribute = true
      } else {
        params.auto_distribute = false
      }
    }

    if (this.filter.marketplace_ids.length && this.filter.distribution_status) {
      params.marketplace_statuses = []
      switch (this.filter.distribution_status) {
        case DistributionStatusLabel.Transferring:
          params.marketplace_statuses.push({
            marketplace_transferring: this.filter.marketplace_ids,
          })
          break
        case DistributionStatusLabel.Transferred:
          params.marketplace_statuses.push({
            marketplace_transferred: this.filter.marketplace_ids,
          })
          break
        case DistributionStatusLabel.TransferFailed:
          params.marketplace_statuses.push({
            marketplace_transfer_failed: this.filter.marketplace_ids,
          })
          break
        default:
          break
      }
    } else {
      params.marketplace_statuses = []
    }

    if (this.filter.detector) {
      const value = this.filter.detector

      if (value === Detector.None) {
        params.ai_detected = undefined
      } else if (value === Detector.NotAIgenerated) {
        params.ai_detected = false
      } else if (value === Detector.AIgenerated) {
        params.ai_detected = true
      }
    }

    if (this.filter.user_email?.length) {
      params.user_email = this.filter.user_email
    }

    if (this.filter.reviewer_email?.length) {
      params.reviewer_email = this.filter.reviewer_email
    }

    if (this.filter.title?.length) {
      params.title = this.filter.title
    }

    if (this.filter.keywords?.length) {
      params.keywords = this.filter.keywords
    }

    if (this.filter.assignment?.length) {
      params.assignment = this.filter.assignment
    }

    if (this.filter.import_guid?.length) {
      params.import_guid = this.filter.import_guid
    }

    if (this.filter.source_file_fingerprint?.length) {
      params.source_file_fingerprint = this.filter.source_file_fingerprint
    }

    return params as
      | GetApiV1ResourcesQueryParams
      | GetApiV1AdminResourcesSearchQueryParams
  }
}
